import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { History as FeedIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { relativeDate } from '../../../../utils/dateUtils';
import { useVenue } from '../../../../context/VenueContext';
import inventoryFeedService from '../../../../services/inventoryFeedService';
import venueLocationService from '../../../../services/venueLocationService';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
import type { MovementFeedRow, MovementType, VenueLocation } from '../../../../types/venue';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const COL_COUNT = 6; // Wine, Type, Qty, Location, Date, Source

const MOVEMENT_TYPE_OPTIONS: { value: MovementType; label: string }[] = [
  { value: 'RECEIVE',            label: 'Receive' },
  { value: 'DEPLETE',            label: 'Deplete' },
  { value: 'TRANSFER_IN',        label: 'Transfer In' },
  { value: 'TRANSFER_OUT',       label: 'Transfer Out' },
  { value: 'COUNT_VARIANCE',     label: 'Count Variance' },
  { value: 'MANUAL_ADJUSTMENT',  label: 'Adjustment' },
  { value: 'WASTE',              label: 'Waste' },
  { value: 'RETURN_TO_SUPPLIER', label: 'Return to Supplier' },
];

const SOURCE_DOC_LABELS: Record<string, string> = {
  GOODS_RECEIPT:  'Receipt',
  PURCHASE_ORDER: 'PO',
  STOCK_COUNT:    'Stock Count',
  TRANSFER:       'Transfer',
  MANUAL:         'Manual',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function signedQty(qty: number): string {
  return qty > 0 ? `+${qty}` : String(qty);
}

function qtyColor(qty: number): string {
  if (qty > 0) return '#1a7f37';
  if (qty < 0) return '#cf222e';
  return '#6e7781';
}

function fmtSource(doc: MovementFeedRow['sourceDocument']): string {
  if (!doc) return '—';
  const label = SOURCE_DOC_LABELS[doc.type] ?? doc.type;
  // Show last 6 chars of ID as a compact reference
  const shortId = doc.id.length > 6 ? `·\u202f${doc.id.slice(-6)}` : '';
  return `${label}${shortId}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryMovementFeed: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { venueId } = useVenue();

  // ── Filter state — initialise type from URL so dashboard can link with preset ─
  const [typeFilter, setTypeFilter] = useState<MovementType | ''>(() => {
    const t = searchParams.get('type') as MovementType | null;
    return MOVEMENT_TYPE_OPTIONS.some(o => o.value === t) ? (t as MovementType) : '';
  });
  const [locationFilter, setLocationFilter] = useState('');

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<MovementFeedRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0); // MUI 0-based; API 1-based
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [locations, setLocations] = useState<VenueLocation[]>([]);

  // ── Load locations once ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(setLocations)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Load feed ─────────────────────────────────────────────────────────────────
  const loadFeed = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryFeedService.getFeed(venueId, {
        page: page + 1,
        pageSize,
        type: typeFilter || undefined,
        locationId: locationFilter || undefined,
      });
      setRows(result.data ?? []);
      setTotalItems(result.pagination?.totalItems ?? 0);
    } catch {
      setError('Failed to load movement feed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, typeFilter, locationFilter]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // ── Column header style ───────────────────────────────────────────────────────
  const colStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.72rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Header */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FeedIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Movement History</Typography>
          {!loading && totalItems > 0 && (
            <Typography variant="caption" color="text.secondary">({totalItems})</Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
          All stock changes across all items · transfers appear as separate stock-out and stock-in entries
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Movement type</InputLabel>
          <Select
            value={typeFilter}
            label="Movement type"
            onChange={e => { setTypeFilter(e.target.value as MovementType | ''); setPage(0); }}
          >
            <MenuItem value="">All types</MenuItem>
            {MOVEMENT_TYPE_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={locationFilter}
            label="Location"
            onChange={e => { setLocationFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Wine', 'Type', 'Qty', 'Location', 'Date', 'Source'].map(h => (
                  <TableCell key={h} sx={colStyle}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COL_COUNT}>
                    <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary">Loading movement history…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COL_COUNT}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <FeedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.25 }} />
                      <Typography variant="body2">
                        {typeFilter || locationFilter
                          ? 'No movements match the current filters.'
                          : 'No movements yet.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow key={`${row._id}-${index}`} hover>
                    {/* Wine */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.displayName ?? '—'}
                      </Typography>
                      {row.producerName && (
                        <Typography variant="caption" color="text.secondary">
                          {row.producerName}
                        </Typography>
                      )}
                    </TableCell>
                    {/* Type */}
                    <TableCell>
                      <MovementTypeBadge type={row.type} />
                    </TableCell>
                    {/* Qty */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: qtyColor(row.quantity), fontFamily: 'monospace' }}
                      >
                        {signedQty(row.quantity)}
                      </Typography>
                    </TableCell>
                    {/* Location */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.locationName ?? '—'}
                      </Typography>
                    </TableCell>
                    {/* Date */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                        {relativeDate(row.postedAt)}
                      </Typography>
                    </TableCell>
                    {/* Source */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                      >
                        {fmtSource(row.sourceDocument)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalItems > pageSize && (
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        )}
      </Paper>

    </Box>
  );
};

export default InventoryMovementFeed;
